'use client';

import Button, { ButtonType } from '../../../../components/button/Button';
import { FilterType, IAccessoryFilter, IFacetProps } from '../../interfaces';
import { IActions, IState } from '../../actions';
import { useCallback, useEffect, useState } from 'react';

import {
  Collapse,
  CollapseSize,
} from '../../../../components/collapsible/collapsible';
import { FilterFieldRenderer } from './FilterFieldRenderer';
import FilterSlideout from '../../../../components/slideout/filters-mobile/filtersMobileSlideout';
import { IconType } from '../../../../components/icon/Icon';
import { hookIsMobile } from '../../hooks/hookIsMobile';
import { useReactHookz } from '../../state';

export const FilterComponent = () => {
  const [state, actions]: [IState, IActions] = useReactHookz();
  const isMobile = hookIsMobile('--from-desktop');
  const [mobileVisible, setMobileVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (state.contentHubData?.facets) {
      setIsLoading(false);
    }
  }, [state.contentHubData?.facets]);

  const systemSelectorLabels =
    state.configuration.smartSystemSelectorContent?.[0]?.systemSelectorLabels;

  const handleFacetSelect = useCallback(
    (item: IAccessoryFilter, checked: boolean) => {
      // for slider add filter on change
      if (
        state.contentHubData?.facets?.some(
          (f: IFacetProps) =>
            f.filterType === FilterType.SLIDER && !f.hideThisValues
        ) &&
        item.filterType === FilterType.SLIDER
      ) {
        actions.addAccessoryFilter(item, true);
        return;
      }

      if (checked) {
        actions.addAccessoryFilter(item, true);
      } else {
        actions.removeAccessoryFilter(item);
      }
    },
    [state.contentHubData?.facets, actions]
  );

  const renderFilters = () => {
    if (isLoading) {
      return Array.from({ length: 3 }).map((_, index) => (
        <Collapse
          key={`skeleton-${index}`}
          renderPartialSkeleton={true}
          size={CollapseSize.TINY}
        />
      ));
    }

    return state.contentHubData.facets.map(
      (filter: IFacetProps, index) =>
        !filter.hideThisValues && (
          <Collapse
            id={filter.id}
            key={index}
            moreLabel={systemSelectorLabels?.moreLabel}
            lessLabel={systemSelectorLabels?.lessLabel}
            {...filter.collapse}
            renderPartialSkeleton={isLoading}
            size={CollapseSize.TINY}
            onClickTooltip={() => {
              if (filter.props.enableFilterInfoPanel) {
                actions.setInfoPanel(
                  true,
                  filter.props.label,
                  filter.props.attributeConfig?.richTextBlocks[0]
                    ?.staticContent || []
                );
              }
            }}
          >
            <FilterFieldRenderer
              filterType={filter.filterType}
              content={filter.content}
              props={(filter as any).props}
              collapse={filter.collapse}
              selectedFacets={state.selectedFacets}
              onSelect={handleFacetSelect}
            />
          </Collapse>
        )
    );
  };

  if (isMobile) {
    return (
      <>
        <Button
          type={ButtonType.TERTIARY}
          label="Filters"
          icon={IconType.Filters}
          onClick={() => setMobileVisible(true)}
          extraClasses="filter-sort-buttons mobile-buttons"
        />
        <FilterSlideout
          isVisible={mobileVisible}
          onClose={() => setMobileVisible(false)}
          onClear={() => actions.clearAccessoryFilters()}
          onApply={() => setMobileVisible(false)}
        >
          {renderFilters()}
        </FilterSlideout>
      </>
    );
  }

  return <>{renderFilters()}</>;
};
